import { Box, SxProps, Theme } from '@mui/material';
import {
    ReactNode,
    useCallback,
    useLayoutEffect,
    useRef,
    useState,
    WheelEvent,
} from 'react';

/** How far, in px, the cut-off edge fades before the content is full. */
const FADE_PX = 24;

/** Which edges have content cut off past them. */
interface CutEdges {
    readonly start: boolean;
    readonly end: boolean;
}

function cutEdgesOf(node: HTMLElement): CutEdges {
    return {
        start: node.scrollLeft > 0,
        // A sub-pixel remainder at the end still counts as fitting.
        end: node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
    };
}

function overflowToken({ start, end }: CutEdges): string {
    const tokens = [start ? 'start' : '', end ? 'end' : ''].filter(Boolean);
    return tokens.length > 0 ? tokens.join(' ') : 'none';
}

const FADE_TO_END = `linear-gradient(to right, #000 calc(100% - ${FADE_PX}px), transparent)`;
const FADE_FROM_START = `linear-gradient(to right, transparent, #000 ${FADE_PX}px)`;
const FADE_BOTH = `linear-gradient(to right, transparent, #000 ${FADE_PX}px, #000 calc(100% - ${FADE_PX}px), transparent)`;

function masked(gradient: string) {
    return { maskImage: gradient, WebkitMaskImage: gradient };
}

interface OverflowScrollRowProps {
    readonly children: ReactNode;
    readonly sx?: SxProps<Theme>;
    readonly 'data-testid'?: string;
}

/**
 * A row of controls that scrolls sideways once they no longer fit, instead
 * of wrapping or pushing its neighbours out. No scrollbar is drawn; the
 * edge with content cut off past it fades out, so the reader sees there is
 * more, and the fade goes when there is not. A vertical wheel over the row
 * scrolls it sideways while it is cut off, so a mouse can reach the far
 * controls; focus moving to a hidden control brings it into view as any
 * scroll container does. The `data-overflow` attribute names the cut
 * edges: `none`, `start`, `end` or `start end`.
 */
export default function OverflowScrollRow({
    children,
    sx,
    'data-testid': testId,
}: OverflowScrollRowProps) {
    const ref = useRef<HTMLDivElement>(null);
    const [cut, setCut] = useState<CutEdges>({ start: false, end: false });

    const measure = useCallback(() => {
        const node = ref.current;
        if (!node) {
            return;
        }
        const next = cutEdgesOf(node);
        setCut((previous) =>
            previous.start === next.start && previous.end === next.end
                ? previous
                : next
        );
    }, []);

    // The cut edges follow the row's width and its children's widths: a
    // control that appears or grows can push the rest past the edge with
    // no change to the row itself, so the children are observed too.
    useLayoutEffect(() => {
        const node = ref.current;
        if (!node) {
            return;
        }
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(node);
        for (const child of Array.from(node.children)) {
            observer.observe(child);
        }
        return () => observer.disconnect();
    });

    const handleWheel = (event: WheelEvent<HTMLDivElement>) => {
        const node = ref.current;
        if (
            !node ||
            event.deltaX !== 0 ||
            node.scrollWidth <= node.clientWidth
        ) {
            return;
        }
        node.scrollLeft += event.deltaY;
    };

    return (
        <Box
            ref={ref}
            data-testid={testId}
            data-overflow={overflowToken(cut)}
            onScroll={measure}
            onWheel={handleWheel}
            sx={[
                {
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    '& > *': { flexShrink: 0 },
                    '&[data-overflow="end"]': masked(FADE_TO_END),
                    '&[data-overflow="start"]': masked(FADE_FROM_START),
                    '&[data-overflow="start end"]': masked(FADE_BOTH),
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            {children}
        </Box>
    );
}
